import { Link } from 'react-router-dom';
import PageHero from '../../components/feature/PageHero';
import SellCarForm from './components/SellCarForm';
import HowItWorks from './components/HowItWorks';
import SellCarComparison from './components/SellCarComparison';
import WhatWeBuy from './components/WhatWeBuy';
import WhyBuyFromUs from '../../components/feature/WhyBuyFromUs';
import BuyAndSellFeatures from './components/BuyAndSellFeatures';
import SellCarFaq from './components/SellCarFaq';
import SellCarCta from './components/SellCarCta';
import { useEffect } from 'react';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import { injectFAQSchema, removeFAQSchema } from '../../lib/safeSchema';
import { pageFaqs } from '../../mocks/pageFaqs';
import { useSEOHead } from '../../hooks/useSEOHead';

export default function SellYourCarPage() {
  useSEOHead({
    title: 'Sell Your Car Warrenton OR | Fast Offer | Rockhound Auto',
    description: 'Sell your car in Warrenton, OR without the dealer lowball. Fair cash offers in 24 hours. No pressure, no games. Serving Astoria, Seaside, and the Oregon Coast.',
    keywords: 'sell car Warrenton OR, sell used car Oregon Coast, cash for cars Warrenton, sell car Astoria OR',
    canonicalUrl: 'https://rockhoundauto.com/sell-your-car',
  });

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Sell Your Car',
          item: 'https://rockhoundauto.com/sell-your-car',
        },
      ],
    };

    injectJsonLd('schema-sell-your-car-breadcrumb', breadcrumbSchema);

    injectFAQSchema(
      'schema-sell-your-car-faq',
      pageFaqs.sellYourCar,
      'https://rockhoundauto.com/sell-your-car#faqpage'
    );

    return () => {
      removeJsonLd('schema-sell-your-car-breadcrumb');
      removeFAQSchema('schema-sell-your-car-faq');
    };
  }, []);

  return (
    <>
      <PageHero
        theme="brand"
        eyebrow={{
          icon: 'ri-hand-coin-line',
          text: 'Fair Offers in 24 Hours'
        }}
        title={
          <>
            Sell Your Car
            <span className="block text-[#E8B84B]">Without the Dealer Lowball</span>
          </>
        }
        subtitle={
          <>
            No pressure. No games. Just a fair cash offer based on real market data —
            not what a dealer thinks they can flip it for. Get a real offer that reflects your vehicle's actual{' '}
            <Link to="/trade-in" className="text-[#E8B84B] hover:text-[#D4A43A] font-semibold underline decoration-2 underline-offset-2">
              trade-in value
            </Link>
            , not a lowball wholesale number.
          </>
        }
        ctas={[
          { label: 'Get Your Offer Now', href: '/sell-your-car#form', variant: 'primary' },
          { label: 'Trade-In Instead?', href: '/trade-in', variant: 'secondary' }
        ]}
      >
        {/* Trust Signals Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E8E2D6]">
            <div className="w-12 h-12 bg-[#1A2332]/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
              <i className="ri-timer-flash-line text-2xl text-[#1A2332]"></i>
            </div>
            <h3 className="font-bold text-[#1A2332] mb-2">Offer in 24 Hours</h3>
            <p className="text-sm text-[#4A5568]">
              Submit your info today, get a real cash offer tomorrow. No waiting around.
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E8E2D6]">
            <div className="w-12 h-12 bg-[#E8B84B]/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
              <i className="ri-hand-coin-line text-2xl text-[#E8B84B]"></i>
            </div>
            <h3 className="font-bold text-[#1A2332] mb-2">No Dealer Lowball</h3>
            <p className="text-sm text-[#4A5568]">
              We pay market value — not &quot;wholesale minus $2,000 because we need to make a profit.&quot;
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E8E2D6]">
            <div className="w-12 h-12 bg-[#1A2332]/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
              <i className="ri-thumb-up-line text-2xl text-[#1A2332]"></i>
            </div>
            <h3 className="font-bold text-[#1A2332] mb-2">Zero Obligation</h3>
            <p className="text-sm text-[#4A5568]">
              Get an offer. Think it over. Accept or walk away. No pressure, no follow-up spam.
            </p>
          </div>
        </div>

        {/* Location-specific social proof */}
        <p className="text-base text-[#4A5568] mt-8 leading-relaxed">
          Sellers from{' '}
          <Link to="/service-areas/astoria" className="text-[#E8B84B] hover:text-[#D4A43A] font-semibold underline decoration-2 underline-offset-2">
            Astoria
          </Link>
          ,{' '}
          <Link to="/used-cars-tillamook-oregon" className="text-[#E8B84B] hover:text-[#D4A43A] font-semibold underline decoration-2 underline-offset-2">
            Tillamook
          </Link>
          , and{' '}
          <Link to="/used-cars-sw-washington" className="text-[#E8B84B] hover:text-[#D4A43A] font-semibold underline decoration-2 underline-offset-2">
            SW Washington
          </Link>{' '}
          have used this process to sell without the dealership runaround.
        </p>
      </PageHero>

      <SellCarForm />
      <HowItWorks />
      <SellCarComparison />
      <WhatWeBuy />
      <WhyBuyFromUs variant="seller" />
      <BuyAndSellFeatures />
      <SellCarFaq />
      <SellCarCta />
    </>
  );
}